import { useContext } from 'react';
import type { SyntheticEvent } from 'react';

import { CardActionContext } from './context';

/**
 * Хук для обработки действий карточки с правилами на основе условий
 *
 * Этот хук предоставляет гибкий способ обработки взаимодействий с карточками,
 * позволяя условное выполнение различных действий в зависимости от переданных опций.
 * Он проверяет зарегистрированные правила в контексте и выполняет действие первого подходящего правила.
 * Если ни одно правило не подходит, используется переданное базовое действие.
 *
 * @template TOptions - Тип объекта опций, используемого для условий правил
 *
 * @param {function(SyntheticEvent): void} basicAction - Действие по умолчанию, которое выполняется когда нет подходящих правил.
 *   Эта функция получает React SyntheticEvent и должна содержать базовую логику взаимодействия.
 *   Пример: (event) => { event.preventDefault(); console.log('Действие по умолчанию'); }
 *
 * @param {TOptions} options - Объект конфигурации, используемый для проверки условий правил.
 *   Каждая функция-условие правила получает эти опции чтобы определить должно ли правило выполниться.
 *   Обычно включает данные карточки: оффер, позиция, информация об устройстве и т.д.
 *   Пример: { offer: { id: '123', type: 'premium' }, position: 1, isMobile: false }
 *
 * @returns {function(SyntheticEvent): void} - Функция-обработчик события, которая:
 *   1. Проверяет все зарегистрированные правила с переданными опциями
 *   2. Выполняет действие первого подходящего правила если оно найдено
 *   3. Возвращается к basicAction если нет подходящих правил
 *   4. Может быть прикреплена напрямую к обработчикам onClick, onTouch и т.д.
 *
 * @throws Использует basicAction если CardActionProvider не найден в дереве компонентов
 *
 * @example
 * // Базовое использование с данными оффера
 * const action = useCardAction(
 *   (event) => {
 *     event.preventDefault();
 *     navigateToOffer(offer.id);
 *   },
 *   { offer, position: index, isMobile }
 * );
 *
 * @example
 * // С несколькими опциями для сложных условий
 * const action = useCardAction(
 *   handleDefaultClick,
 *   {
 *     offer: currentOffer,
 *     user: userProfile,
 *     context: { listType: 'search', hasPromo: true },
 *     device: { isMobile, isTouchable }
 *   }
 * );
 *
 * @example
 * // В JSX
 * <div onClick={action} className="card">
 *   Контент карточки
 * </div>
 *
 * @see {@link CardActionProvider} для настройки контекста правил
 * @see {@link RuleAction} для структуры определения правил
 */

// такая заглушка добавлена как хак чтобы не ломать логику страниц
const nonActionBinding = (_event: SyntheticEvent) => {
  // empty
};

export function useCardAction<TOptions>(
  basicAction: (event: SyntheticEvent) => void = nonActionBinding,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  options: TOptions = {},
) {
  const rules = useContext(CardActionContext);

  if (!rules) {
    return basicAction;
  }
  const matchedRule = rules?.find((rule) => rule.condition(options));

  return (event: SyntheticEvent) => {
    if (matchedRule) {
      matchedRule.action(event, options);
      return;
    }
    return basicAction(event);
  };
}
